import React, { useState } from "react";
import "./project.scss";

/**
 * Custom Project Allocation Chart Component using SVG
 * Simple donut chart showing allocation breakdown
 * @param {string} allocation - Comma-separated allocation values (e.g., "10,10,70,10")
 * @param {string} workCategoryName - Comma-separated category names
 * @param {number} projectAllocation - Total project allocation percentage to display in center
 * @param {function} onEngagementEdit - Callback function to handle engagement edit
 * @param {Object} projectData - Project data to pass to the engagement edit callback
 * @author Sejal
 */
const ProjectAllocationChart = ({ allocation = "", workCategoryName = "", projectAllocation, onEngagementEdit, projectData }) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const size = 50;
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;

  // Base colors from CSS classes
  const baseColors = [
    "#1f509a", // segment-0
    "#478ccf", // segment-1
    "#36c2ce", // segment-2
    "#77e4c8", // segment-3
  ];

  // Function to generate similar shades when more segments are needed
  const generateSegmentColor = (index) => {
    if (index < baseColors.length) {
      return baseColors[index];
    }
    // For segments beyond the base colors, use the additional colors defined in CSS
    const additionalColors = [
      "#2a5db3", // segment-4
      "#5aa0d9", // segment-5
      "#4ad4da", // segment-6
      "#8ae8d4", // segment-7
    ];

    if (index < baseColors.length + additionalColors.length) {
      return additionalColors[index - baseColors.length];
    }

    // If we need even more colors, generate variations
    const baseIndex = index % baseColors.length;
    const variation = Math.floor(index / baseColors.length);

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    };

    const rgbToHex = (r, g, b) => {
      const clamp = (num) => Math.max(0, Math.min(255, Math.round(num)));
      return "#" + ((1 << 24) + (clamp(r) << 16) + (clamp(g) << 8) + clamp(b)).toString(16).slice(1);
    };

    const baseColor = baseColors[baseIndex];
    const rgb = hexToRgb(baseColor);
    if (!rgb) return baseColor;

    const factor = variation % 2 === 0 ? 1.2 : 0.8;
    const adjustedR = rgb.r * factor;
    const adjustedG = rgb.g * factor;
    const adjustedB = rgb.b * factor;

    return rgbToHex(adjustedR, adjustedG, adjustedB);
  };

  const segments = React.useMemo(() => {
    if (!allocation || !workCategoryName) return [];

    const allocations = allocation.split(",").map((val) => parseFloat(val.trim()));
    const categories = workCategoryName.split(",").map((cat) => cat.trim());
    const total = allocations.reduce((sum, val) => sum + val, 0);

    if (total === 0) return [];

    let currentAngle = 0;

    return allocations
      .map((value, idx) => {
        if (value <= 0) return null;

        const percentage = (value / total) * 100;
        const startAngle = currentAngle;
        const endAngle = currentAngle + (value / total) * 360;
        currentAngle = endAngle;

        const startAngleRad = ((startAngle - 90) * Math.PI) / 180;
        const endAngleRad = ((endAngle - 90) * Math.PI) / 180;

        const x1 = size / 2 + radius * Math.cos(startAngleRad);
        const y1 = size / 2 + radius * Math.sin(startAngleRad);
        const x2 = size / 2 + radius * Math.cos(endAngleRad);
        const y2 = size / 2 + radius * Math.sin(endAngleRad);

        const largeArcFlag = percentage > 50 ? 1 : 0;
        const pathData = `M ${size / 2} ${size / 2} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

        return {
          category: categories[idx] || `Category ${idx + 1}`,
          value: value,
          color: generateSegmentColor(idx),
          pathData,
        };
      })
      .filter(Boolean);
  }, [allocation, workCategoryName]);

  const handleMouseEnter = (segment) => {
    setHoveredSegment(segment);
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleSegmentClick = () => {
    if (onEngagementEdit && projectData) {
      onEngagementEdit(projectData);
    }
  };

  const handleChartClick = () => {
    if (onEngagementEdit && projectData) {
      onEngagementEdit(projectData);
    }
  };

  if (!segments || segments.length === 0) {
    return (
      <div className='project-allocation-chart position-relative'>
        <div className='no-data-container' onClick={handleChartClick} style={{ cursor: onEngagementEdit ? "pointer" : "default" }}>
          {projectAllocation || 0}%
        </div>
      </div>
    );
  }
  return (
    <div
      className='project-allocation-chart position-relative'
      onMouseMove={handleMouseMove}
      onClick={handleChartClick}
      style={{ cursor: onEngagementEdit ? "pointer" : "default" }}>
      <div className='position-relative cursor-pointer'>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Background circle */}

          {/* Segments */}
          {segments.map((segment, index) => (
            <path
              key={index}
              d={segment.pathData}
              fill={segment.color}
              stroke='white'
              strokeWidth={1}
              className={`segment-${index % 8}`}
              onMouseEnter={() => handleMouseEnter(segment)}
              onMouseLeave={() => setHoveredSegment(null)}
              style={{ cursor: onEngagementEdit ? "pointer" : "default" }}
            />
          ))}

          {/* Inner white circle for donut effect */}
          <circle cx={size / 2} cy={size / 2} r={radius - strokeWidth} fill='white' />
        </svg>

        {/* Center text */}
        <div className='center-text'>{projectAllocation || 0}%</div>

        {/* Tooltip */}
        {hoveredSegment && (
          <div
            className='chart-tooltip'
            style={{
              left: `${mousePosition.x + 10}px`,
              top: `${mousePosition.y - 30}px`,
            }}>
            {hoveredSegment.category}: {hoveredSegment.value}%
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectAllocationChart;
